import React, { useCallback, useEffect, useMemo, useState } from "react";
import { animated, config, useTransition } from "react-spring";
import { isMobile } from "react-device-detect";
import nookies from "nookies";
import styled from "styled-components";
import { InferGetServerSidePropsType } from "next";
import Link from "next/link";
import Image from "next/image";
import { useRouter } from "next/router";
import { useForm, Controller } from "react-hook-form";
import InputAdornment from "@material-ui/core/InputAdornment";
import Box from "@material-ui/core/Box";
import Grid from "@material-ui/core/Grid";

import { useAuth } from "domain/auth/authContext";
import { useCommunity } from "domain/community/communityContext";
import CommunityService from "domain/community/communityService";
import PageLayout from "lib/components/PageLayout";
import Wrapper from "lib/components/Wrapper";
import Post from "lib/components/Post";
import CreateEditPostModal from "lib/components/CreateEditPostModal";
import ReportModal from "lib/components/ReportModal";
import { Menu, MenuItem } from "lib/components/Menu";
import Comment from "lib/components/Comment";
import Spin from "lib/components/Spin";
import Avatar from "lib/components/Avatar";
import TextField from "lib/components/TextField";
import {
  AsyncStatus,
  CustomEventLabel,
  KeyboardKey,
  ObjectEvent,
  UIEvent,
} from "lib/enums";
import { useTheme } from "lib/contexts/themeContext";
import { useLayout } from "lib/contexts/layoutContext";
import IconButton from "lib/components/IconButton";
import IconClose from "lib/assets/close-modal.svg";
import IconSearch from "lib/assets/search-bold.svg";
import IconWritePost from "lib/assets/write.svg";
import IconExclamation from "lib/assets/exclamation.svg";
import IconEdit from "lib/assets/edit.svg";
import IconDelete from "lib/assets/trash.svg";
import IconChevronRight from "lib/assets/chevron-right.svg";
import useIsResMobile from "lib/hooks/useIsResMobile";
import useKeyPress from "lib/hooks/useKeyPress";
import InputSearch from "domain/community/InputSearch";
import HashtagLink from "domain/community/HashtagLink";
import { useEvent } from "lib/contexts/eventContext";
import Head from "lib/components/Head";
import IconLogo from "lib/assets/logo.svg";
import Button from "lib/components/Button";
import BannerUSCISStatusExplorer from "lib/assets/banner-uscis-status-explorer-community-small.png";
import { purify } from "lib/format";
import { getAirbridgeTrackerLink } from "lib/airbridge";
import AdSlot from "lib/components/AdSlot";
import { useTranslations } from "lib/i18n";
import { useCategoryLabel } from "domain/community/useCategoryLabel";

const PAGE_SIZE = 100;

const PostDetail = (
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
) => {
  const t = useTranslations("community");
  const {
    state: { dataUser, dataProfile },
  } = useAuth();
  const {
    state: { dataListTagPopularByCategory, dataListCommunityCategory, status },
    doGetListCommunityCategory,
    doGetPost,
    doCreatePost,
    doEditPost,
    doDeletePost,
    doReportPost,
    doGetListComment,
    doCreateComment,
    doDeleteComment,
    doUpdateListTagPopularByCategory,
    doSetHasPostedInSession,
  } = useCommunity();
  const theme = useTheme();
  const isResMobile = useIsResMobile();
  const { doOpenAlert } = useLayout();
  const { track } = useEvent();
  const router = useRouter();
  const { query } = router;
  const [isWritePostModalOpen, setIsWritePostModalOpen] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [dataPost, setDataPost] = useState<
    ResCommunityPostPost | ResCommunityPostNews | null
  >(props.post || null);
  const [dataListComment, setDataListComment] = useState<
    ResCommunityCommentWithPagination | undefined
  >(props.comments);
  const [statusPost, setStatusPost] = useState(AsyncStatus.Initial);
  const postId = dataPost?.postId;
  const postSlug = dataPost?.urlSlug;
  const category = useMemo(() => {
    if (!dataPost) return null;
    return dataListCommunityCategory.find(
      (c) => c.categoryId === dataPost.categoryId,
    );
  }, [dataListCommunityCategory, dataPost]);
  const currentCategoryPopularTags = useMemo(
    () => dataListTagPopularByCategory[category?.urlSlug] ?? [],
    [dataListTagPopularByCategory, category?.urlSlug],
  );

  const handleOnWriteClick = useCallback(() => {
    if (!dataUser) {
      router.push({
        pathname: "/login",
        query: {
          redirect: router.asPath,
        },
      });
    } else if (!dataProfile) {
      doOpenAlert({
        content:
          "You need to create a profile to write a post.\nWould you like to create it now?",
        confirmMessage: t("createPrompt.confirm"),
        cancelMessage: t("createPrompt.cancel"),
        onConfirm: () => {
          router.push({
            pathname: "/profile",
            query: {
              redirect: router.asPath,
            },
          });
        },
        onCancel: () => {
          // noop
        },
      });
    } else {
      setIsWritePostModalOpen(true);
    }
  }, [dataProfile, dataUser, doOpenAlert, router]);

  const handleOnWriteModalClose = useCallback(() => {
    setIsWritePostModalOpen(false);
  }, []);

  const getListTagPopular = useCallback(async () => {
    const data = await new CommunityService().getListTag({
      category: String(category?.urlSlug),
    });
    doUpdateListTagPopularByCategory(category?.urlSlug, data);
  }, [category?.urlSlug, doUpdateListTagPopularByCategory]);

  const getPost = useCallback(async () => {
    setStatusPost(AsyncStatus.Requested);
    try {
      const data = await doGetPost(postSlug);
      setDataPost(data);
      setStatusPost(AsyncStatus.Success);
    } catch (e) {
      setStatusPost(AsyncStatus.Error);
    }
  }, [doGetPost, postSlug]);

  const getListComment = useCallback(async () => {
    try {
      const data = await doGetListComment({
        postSlug,
        pageNumber: Number(router.query.page) || 1,
        pageSize: PAGE_SIZE,
      });
      setDataListComment(data);
    } catch (e) {
      // noop
    }
  }, [doGetListComment, postSlug, router.query.page]);

  const refresh = useCallback(() => {
    return Promise.all([getPost(), getListComment()]);
  }, [getPost, getListComment]);

  const handleOnPostEdit = useCallback(
    async (data: {
      postId: number;
      categoryId: number;
      title: string;
      contents: string;
    }) => {
      try {
        const post = await doEditPost(data);
        setDataPost(post);
      } catch (e) {
        throw e;
      }
    },
    [doEditPost],
  );

  const handleOnPostDelete = useCallback(async () => {
    setIsDisabled(true);
    try {
      await doDeletePost(postId);
      router.push("/community");
    } catch (e) {
      setIsDisabled(false);
      doOpenAlert({
        content: t("detail.deleteFailed"),
        isError: true,
      });
    }
  }, [doDeletePost, postId, doOpenAlert, router]);

  const handleOnPostReport = useCallback(
    async (data: { title: string; contents: string }) => {
      try {
        const post = await doReportPost({ postId, ...data });
        setDataPost(post);
      } catch (e) {
        doOpenAlert({
          content: t("detail.reportFailed"),
          isError: true,
        });
      }
    },
    [doReportPost, doOpenAlert, postId],
  );

  const handleOnCommentSubmit = useCallback(
    async (comment: string) => {
      setIsDisabled(true);
      try {
        const { commentId } = await doCreateComment(postId, comment);
        track(ObjectEvent.CommunityCommentCreated, {
          postId,
          commentId,
        });
        await refresh();
        setIsDisabled(false);
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: "smooth",
        });
        doSetHasPostedInSession();
      } catch (e) {
        setIsDisabled(false);
      }
    },
    [doCreateComment, postId, track, refresh, doSetHasPostedInSession],
  );

  const handleOnCommentDelete = useCallback(
    async (commentId: number) => {
      setIsDisabled(true);
      try {
        await doDeleteComment(commentId);
        await refresh();
        setIsDisabled(false);
      } catch (e) {
        setIsDisabled(false);
      }
    },
    [doDeleteComment, refresh],
  );

  const handleOnPostCreate = useCallback(
    async (data: { categoryId: number; title: string; contents: string }) => {
      try {
        const post = await doCreatePost(data);
        track(ObjectEvent.CommunityPostCreated, {
          postId: post.postId,
          category: post.categoryId,
        });
        const slug = encodeURIComponent(post.urlSlug);
        await router.push(
          { pathname: `/community/posts/[postId]` },
          { pathname: `/community/posts/${slug}` },
        );
      } catch (e) {
        throw e;
      }
    },
    [doCreatePost, router, track],
  );

  useEffect(() => {
    setDataPost(props.post);
  }, [props.post]);

  useEffect(() => {
    setDataListComment(props.comments);
  }, [props.comments]);

  useEffect(() => {
    if (status.dataListCommunityCategory === AsyncStatus.Initial) {
      doGetListCommunityCategory();
    }
  }, [status.dataListCommunityCategory, doGetListCommunityCategory]);

  useEffect(() => {
    if (typeof query.postId === "string" && query.postId.match(/^\d+$/)) {
      router.replace(
        `/community/posts/${dataPost.urlSlug}`,
        `/community/posts/${dataPost.urlSlug}`,
        { shallow: true },
      );
    }
  }, [dataPost?.urlSlug, query.postId, router]);

  useEffect(() => {
    if (category) {
      getListTagPopular();
    }
  }, [category, getListTagPopular]);

  /**
   * Search input states & effects
   *
   * Search bar contains search form (input)
   */

  /* search form */
  const { register, handleSubmit, getValues, setValue } = useForm<{
    keyword: string;
  }>();

  /* search form on submit */
  const handleOnSearch = handleSubmit(({ keyword }) => {
    router.push(
      { pathname: `/community/search/[keyword]` },
      { pathname: `/community/search/${encodeURIComponent(keyword)}` },
    );
  });

  /* search bar */
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);

  /* toggle search bar */
  const handleOnToggleSearch = useCallback(() => {
    setIsSearchEnabled((isSearchEnabled) => !isSearchEnabled);
  }, []);

  const isESCPressed = useKeyPress(KeyboardKey.Escape);

  /* toggle search bar visiblity upon ESC press */
  useEffect(() => {
    if (isESCPressed) {
      if (isSearchEnabled) {
        if (getValues("keyword")) {
          // if there is content, simply reset
          setValue("keyword", "");
        } else {
          // if there is no content, hide search bar
          setIsSearchEnabled(false);
        }
      }
    }
  }, [getValues, isESCPressed, isSearchEnabled, setValue]);

  /**
   * Events related
   */
  const { tag } = useEvent();

  // view event
  useEffect(() => {
    if (postId && tag) {
      tag(CustomEventLabel.ViewCommunityPostDetail);
    }
  }, [tag, postId]);

  const handleOnClickBannerUSCISStatusExplorer = useCallback(() => {
    track(UIEvent.CommunityPostBannerUSCISStatusExplorerClicked);
  }, [track]);

  return (
    <PageLayout
      withAppBanner
      appBannerEvent={UIEvent.CommunityMobileBannerAppDownload}
      withoutFooter
      customCompMobile={
        dataPost?.postType === "post" && (
          <Box
            css={`
              width: 60px;
              height: 60px;
              display: flex;
              align-items: center;
              justify-content: flex-end;
            `}
          >
            <PostMenu
              postId={dataPost.postId}
              isMine={dataPost.isMine}
              categoryId={dataPost.categoryId}
              title={dataPost.post.title}
              contents={dataPost.post.contents}
              onEdit={handleOnPostEdit}
              onDelete={handleOnPostDelete}
              onReport={handleOnPostReport}
            />
          </Box>
        )
      }
    >
      <Wrapper
        css={`
          padding: 0;
        `}
      >
        <Grid
          css={`
            padding-top: ${isResMobile ? 0 : "20px"};
            height: calc(100vh - 70px);
            display: flex;
            flex-direction: column;
          `}
        >
          {!isResMobile && (
            <Grid
              container
              css={`
                border-bottom: ${isResMobile
                  ? `none`
                  : `1px solid ${theme.palette.grey[100]}`};
                position: sticky;
                top: 70px;
                z-index: 9;
                height: 50px;
                background-color: white;
              `}
            >
              {isSearchEnabled && (
                <form action="" onSubmit={handleOnSearch}>
                  <Box
                    css={`
                      position: absolute;
                      width: 100%;
                      height: 100%;
                      z-index: 99;
                      display: flex;
                      align-items: center;
                      background-color: white;
                    `}
                  >
                    <IconSearch
                      css={`
                        margin-right: 10px;
                        cursor: pointer;
                      `}
                      onClick={handleOnSearch}
                    />
                    <InputSearch
                      type="text"
                      placeholder="Enter keyword (ESC to cancel)"
                      name="keyword"
                      ref={register({
                        required: true,
                      })}
                      autoFocus
                    />
                  </Box>
                </form>
              )}
              {/* breadcrumb */}
              <Grid item xs={12} md={10}>
                <Breadcrumb
                  category={
                    dataPost?.postType === "post" ? category : undefined
                  }
                />
              </Grid>
              {/* search and post buttons */}
              <Grid item md={2}>
                <Box display="flex" justifyContent="center">
                  <Box mr="10px">
                    <IconButton onClick={handleOnToggleSearch}>
                      <IconSearch />
                    </IconButton>
                  </Box>
                  <IconButton onClick={handleOnWriteClick}>
                    <IconWritePost />
                  </IconButton>
                </Box>
              </Grid>
            </Grid>
          )}

          {isResMobile && (
            <Breadcrumb
              css={`
                margin-left: 20px;
              `}
              category={dataPost?.postType === "post" ? category : undefined}
            />
          )}

          <Grid
            css={`
              flex: 1;
              height: 100%;
            `}
            container
          >
            <Grid item xs={12} md={8}>
              {dataPost ? (
                <>
                  <Meta post={dataPost} />
                  <Content
                    dataPost={dataPost}
                    dataListComment={dataListComment}
                    disabled={isDisabled}
                    onPostEdit={handleOnPostEdit}
                    onPostDelete={handleOnPostDelete}
                    onPostReport={handleOnPostReport}
                    onCommentDelete={handleOnCommentDelete}
                    onCommentSubmit={handleOnCommentSubmit}
                    onCommentsUpdate={getListComment}
                  />
                </>
              ) : (
                <Loading />
              )}
            </Grid>
            {!isResMobile && (
              <Grid
                item
                md={4}
                css={`
                  border-left: 1px solid ${theme.palette.grey[100]};
                  padding: 40px 0 0 30px;
                `}
              >
                <Box position="sticky" top="145px">
                  <>
                    <h3
                      css={`
                        font-weight: bold;
                        font-size: 16px;
                        line-height: 24px;
                        margin: 0 0 20px;
                      `}
                    >
                      {t("hashtags.title")}
                    </h3>
                    {currentCategoryPopularTags.map((tag) => (
                      <Link
                        key={tag.name}
                        href={{
                          pathname: `/community/tags/[tagId]`,
                          query: {
                            category: String(category.urlSlug),
                          },
                        }}
                        as={{
                          pathname: `/community/tags/${tag.name}`,
                          query: {
                            category: String(category.urlSlug),
                          },
                        }}
                        passHref
                      >
                        <HashtagLink>{`#${tag.name}`}</HashtagLink>
                      </Link>
                    ))}
                  </>
                  {/* uscis status explorer banner */}
                  <div
                    css={`
                      position: sticky;
                      margin-top: 60px;
                      margin-bottom: 20px;
                      cursor: pointer;
                      transition: transform 0.15s ease-in-out;
                      border-radius: 15px;
                      :hover {
                        box-shadow: rgba(0, 0, 0, 0.16) 0px 4px 20px;
                        transform: translate3d(0, -5px, 0);
                      }
                    `}
                  >
                    <a
                      href="/data/uscis-status-explorer"
                      target="_blank"
                      rel="noreferrer"
                      onClick={handleOnClickBannerUSCISStatusExplorer}
                    >
                      <Image
                        src={BannerUSCISStatusExplorer}
                        width={260}
                        height={100}
                        layout="responsive"
                      />
                    </a>
                  </div>
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Wrapper>

      {/* Download App */}
      <DownloadApp />

      {/* Create Post Modal */}
      <CreateEditPostModal
        open={isWritePostModalOpen}
        onClose={handleOnWriteModalClose}
        onSubmit={handleOnPostCreate}
      />
    </PageLayout>
  );
};

export default PostDetail;

const DownloadApp = () => {
  const t = useTranslations("community");
  const {
    state: { hasPostedInSession },
  } = useCommunity();
  const { track } = useEvent();
  const router = useRouter();
  const { query } = router;
  const [isVisible, setIsVisible] = useState(false);
  const transitions = useTransition(isVisible, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: config.stiff,
  });

  const handleOnClose = useCallback(() => {
    setIsVisible(false);
  }, []);

  const handleOnClick = useCallback(() => {
    setIsVisible(false);
    track(UIEvent.CommunityMobileModalAppDownload);
  }, [track]);

  useEffect(() => {
    if (hasPostedInSession) {
      if (isMobile && !sessionStorage.disableDownloadAppOnCommunityPost) {
        setTimeout(() => {
          // Display only once per session.
          sessionStorage.setItem("disableDownloadAppOnCommunityPost", "true");
          setIsVisible(true);
        }, 1000);
      }
    }
  }, [query.postId, router, hasPostedInSession]);

  return transitions(
    (styles, item) =>
      !!item && (
        // eslint-disable-next-line
        // @ts-ignore
        <animated.div
          style={{
            ...styles,
            y: styles.opacity.to([0, 1], [10, 0]),
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
          }}
        >
          <div
            css={`
              margin: 10px;
              border-radius: 10px;
              padding: 10px;
              line-height: 22px;
              font-size: 14px;
              background-color: white;
              box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.16);
            `}
          >
            <div
              css={`
                display: flex;
                align-items: flex-start;
                margin-bottom: 20px;
              `}
            >
              <div
                css={`
                  flex: 1;
                  padding: 10px;
                `}
              >
                {t("detail.notifyPrompt")}
                {t("detail.appDownload")}
              </div>
              <IconButton onClick={handleOnClose}>
                <IconClose
                  css={`
                    path {
                      fill: #999;
                    }
                  `}
                />
              </IconButton>
            </div>
            <div>
              <a
                css={`
                  display: block;
                  border-radius: 10px;
                  padding: 12px 20px;
                  text-align: center;
                  line-height: 24px;
                  font-size: 16px;
                  background-color: ${({ theme }) =>
                    theme.palette.primary.main};
                  color: white;
                  :hover {
                    color: white;
                  }
                `}
                href={getAirbridgeTrackerLink(
                  true,
                  "Community",
                  "On Comment Notification CTA",
                )}
                target="_blank"
                rel="noreferrer"
                onClick={handleOnClick}
              >
                {t("detail.installNow")}
              </a>
            </div>
          </div>
        </animated.div>
      ),
  );
};
interface ContentProps {
  dataPost: ResCommunityPostPost | ResCommunityPostNews;
  dataListComment: ResCommunityCommentWithPagination;
  disabled: boolean;
  onPostEdit: (data: { title: string; contents: string }) => Promise<void>;
  onPostDelete: () => Promise<void>;
  onPostReport: (data: { title: string; contents: string }) => Promise<void>;
  onCommentDelete: (commentId: number) => Promise<void>;
  onCommentSubmit: (comment: string) => Promise<void>;
  onCommentsUpdate: () => Promise<void>;
}

const Content: React.FC<ContentProps> = ({
  dataPost,
  dataListComment,
  disabled,
  onPostEdit,
  onPostDelete,
  onPostReport,
  onCommentDelete,
  onCommentSubmit,
  onCommentsUpdate,
}) => {
  const isResMobile = useIsResMobile();

  return (
    <div
      css={`
        height: 100%;
      `}
    >
      <div
        css={`
          min-height: 100%;
          position: relative;
          display: flex;
          flex-direction: column;
        `}
      >
        <div
          css={`
            flex: 1;
          `}
        >
          {/* Post */}
          {dataPost ? (
            <Box p={isResMobile ? "0 20px" : "10px 20px 10px 0"}>
              <Post
                post={dataPost}
                isDetail
                hideCategory
                menu={
                  dataPost.postType === "post" ? (
                    <PostMenu
                      postId={dataPost.postId}
                      isMine={dataPost.isMine}
                      categoryId={dataPost.categoryId}
                      title={dataPost.post.title}
                      contents={dataPost.post.contents}
                      onEdit={onPostEdit}
                      onDelete={onPostDelete}
                      onReport={onPostReport}
                    />
                  ) : null
                }
              />
            </Box>
          ) : (
            <Loading />
          )}

          <AdSlot
            name="/22876413869/web_community/web_community_post"
            adSizes={[
              [300, 100],
              [468, 60],
            ]}
            css={`
              margin: 20px 0;
            `}
          />

          {/* Comments */}
          <Comments
            comments={dataListComment?.items ?? []}
            onUpdate={onCommentsUpdate}
            onDelete={onCommentDelete}
            uscisTitle={(dataPost as ResCommunityPostPost).uscisTitle}
          />

          {disabled && <Overlay />}
        </div>

        {/* Write comment */}
        {!!dataPost && <WriteComment onSubmit={onCommentSubmit} />}
      </div>
    </div>
  );
};

interface CommentsProps {
  comments: ResCommunityComment[];
  onUpdate: () => Promise<void>;
  onDelete: (commentId: number) => void;
  className?: string;
  uscisTitle?: {
    urlSlug: string;
    uscisTitle: string;
  } | null;
}

const Comments: React.FC<CommentsProps> = ({
  comments,
  onUpdate,
  onDelete,
  className,
  uscisTitle,
}) => {
  const { palette } = useTheme();
  const isResMobile = useIsResMobile();
  const {
    state: { dataUser },
  } = useAuth();

  return (
    <div
      css={`
        position: relative;
        padding: ${isResMobile ? "20px 20px 0 20px" : "30px 20px 0 0"};
        border-top: solid 1px ${palette.grey[100]};
      `}
      className={className}
    >
      {uscisTitle && (
        <BannerUSCISMessageExplorer
          urlSlug={uscisTitle.urlSlug}
          uscisTitle={uscisTitle.uscisTitle}
        />
      )}
      {comments.map((comment) => (
        <Comment
          css={`
            margin-bottom: 40px;
          `}
          key={comment.commentId}
          id={comment.commentId}
          name={comment.name}
          profileImage={comment.profileImage}
          contents={comment.comment}
          createdAt={comment.createdAt}
          isMine={dataUser?.id === comment.userId}
          onUpdate={onUpdate}
          onDelete={onDelete}
          isUserCertified={
            comment.writerType === "lawfully" || comment.writerType === "lawyer"
          }
        />
      ))}
    </div>
  );
};

interface WriteCommentProps {
  onSubmit: (comment: string) => Promise<void>;
  className?: string;
}

const WriteComment: React.FC<WriteCommentProps> = ({ onSubmit, className }) => {
  const t = useTranslations("community");
  const { palette } = useTheme();
  const isResMobile = useIsResMobile();
  const {
    state: { dataUser, dataProfile },
  } = useAuth();
  const { doOpenAlert } = useLayout();
  const { control, formState, getValues, handleSubmit, reset } = useForm<{
    comment: string;
  }>({ defaultValues: { comment: "" } });
  const router = useRouter();
  const { isSubmitting, isDirty } = formState;

  const validate = useCallback(
    () => getValues().comment.trim().length > 0,
    [getValues],
  );

  const handleOnSubmit = handleSubmit(async (data: { comment: string }) => {
    try {
      await onSubmit(purify(data.comment));
      reset();
    } catch (e) {
      // noop
    }
  });

  const handleOnFocus = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      if (!dataUser) {
        e.target.blur();
        router.push({
          pathname: "/login",
          query: {
            redirect: router.asPath,
          },
        });
      } else if (!dataProfile) {
        e.target.blur();
        doOpenAlert({
          content:
            "You need to create a profile to write a comment.\nWould you like to create it now?",
          confirmMessage: t("createPrompt.confirm"),
          cancelMessage: t("createPrompt.cancel"),
          onConfirm: () => {
            router.push({
              pathname: "/profile",
              query: {
                redirect: router.asPath,
              },
            });
          },
          onCancel: () => {
            // noop
          },
        });
      }
    },
    [dataProfile, dataUser, doOpenAlert, router],
  );

  // Focus on comment input field.
  useEffect(() => {
    if (router.query.writeComment) {
      const commentField = document.getElementById("writeComment");
      commentField?.focus();
    }
  }, [router.query.writeComment]);

  return (
    <div
      css={`
        display: flex;
        position: sticky;
        bottom: 0;
        padding: ${isResMobile ? "10px" : "20px 20px 20px 0"};
        border-top: 1px solid ${palette.grey[100]};
        background-color: white;
      `}
      className={className}
    >
      <div
        css={`
          display: flex;
          width: 100%;
        `}
      >
        {!isResMobile && (
          <Avatar
            css={`
              margin-right: 10px;
            `}
            src={dataProfile?.profileImage}
            letter={dataProfile?.firstName[0]}
          />
        )}
        <div
          css={`
            flex: 1;
          `}
        >
          <Controller
            as={TextField}
            control={control}
            name="comment"
            type="text"
            placeholder={t("detail.writeComment")}
            fullWidth
            multiline
            maxRows={5}
            disabled={isSubmitting}
            rules={{ validate }}
            InputProps={{
              inputProps: {
                onFocus: handleOnFocus,
              },
              endAdornment: (
                <StyledInputAdornment position="end">
                  <button
                    css={`
                      border: none;
                      line-height: 22px;
                      padding: 8px 0;
                      font-size: 14px;
                      font-weight: 500;
                      color: ${palette.primary.main};
                      cursor: pointer;
                      background: none;
                      :disabled {
                        color: ${palette.grey[200]};
                      }
                    `}
                    type="submit"
                    disabled={isSubmitting || !isDirty}
                    onClick={handleOnSubmit}
                  >
                    {t("detail.submitComment")}
                  </button>
                </StyledInputAdornment>
              ),
            }}
            id="writeComment"
          />
        </div>
      </div>
    </div>
  );
};

const Loading = () => {
  return (
    <div
      css={`
        display: flex;
        align-items: center;
        justify-content: center;
        height: 250px;
      `}
    >
      <Spin />
    </div>
  );
};

const Overlay = () => {
  return (
    <div
      css={`
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        background-color: rgba(255, 255, 255, 0.6);
      `}
    />
  );
};

const StyledInputAdornment = styled(InputAdornment)`
  &&& {
    position: relative;
    align-self: flex-end;
    bottom: 10px;
    height: 100%;
  }
`;

interface BreadcrumbProps {
  category?: ResCategoryCommunity;
  className?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({ category, className }) => {
  const t = useTranslations("community");
  const categoryLabel = useCategoryLabel();
  const { palette } = useTheme();
  return (
    <Box
      css={`
        height: 50px;
        a {
          line-height: 22px;
          font-size: 14px;
          font-weight: 500;
          color: ${palette.grey[500]};
          :hover {
            color: ${palette.grey[500]};
            text-decoration: underline;
          }
        }
      `}
      className={className}
      display="flex"
      alignItems="center"
    >
      <Link href={`/community/categories/all`}>{t("categories.all")}</Link>
      {!!category && (
        <>
          <IconChevronRight
            css={`
              margin: 0 4px;
            `}
          />
          <Link href={`/community/categories/${category.urlSlug}`}>
            {categoryLabel(category)}
          </Link>
        </>
      )}
    </Box>
  );
};

export const getServerSideProps = async (context) => {
  const { params, query, res } = context;
  const { token } = nookies.get(context);
  const postId = encodeURIComponent(params.postId);
  const service = new CommunityService(token && { token });
  const pageNumber = Number(query.page) || 1;
  try {
    const [post, comments] = await Promise.all([
      service.getPost(postId),
      service.getListCommentPublic({
        postSlug: postId,
        pageNumber,
        pageSize: PAGE_SIZE,
      }),
    ]);
    return {
      props: {
        post,
        comments,
      },
    };
  } catch (e) {
    // Post not found.
    if (e.name === "PostNotFound") {
      res.statusCode = 404;
      return {
        notFound: true,
        props: {
          errorCode: 404,
        },
      };
    }
    // Post deleted.
    if (e.name === "PostIsDeleted") {
      res.statusCode = 410;
      return {
        notFound: true,
        props: {
          errorCode: 410,
        },
      };
    }
    // Something went wrong.
    res.statusCode = 500;
    return {
      props: {
        errorCode: 500,
      },
    };
  }
};

const Meta = ({
  post,
}: {
  post: ResCommunityPostPost | ResCommunityPostNews;
}) => {
  const metaData = getMetaData(post);
  return (
    <Head
      title={metaData.title}
      desc={metaData.description}
      url={metaData.url}
      imageUrl={metaData.image}
    />
  );
};

interface PostMenuProps {
  postId: number;
  isMine: boolean;
  categoryId: number;
  title: string;
  contents: string;
  onEdit?: (data: { title: string; contents: string }) => Promise<void>;
  onDelete?: () => void;
  onReport?: (data: { title: string; contents: string }) => Promise<void>;
}

const PostMenu: React.FC<PostMenuProps> = ({
  postId,
  isMine,
  categoryId,
  title,
  contents,
  onEdit,
  onDelete,
  onReport,
}) => {
  const t = useTranslations("community");
  const {
    state: { dataUser },
  } = useAuth();
  const { doOpenAlert } = useLayout();
  const [isEditPostModalOpen, setIsEditPostModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const handleOnEditClick = useCallback(() => {
    setIsEditPostModalOpen(true);
  }, []);

  const handleOnEditModalClose = useCallback(() => {
    setIsEditPostModalOpen(false);
  }, []);

  const handleOnDeleteClick = useCallback(() => {
    doOpenAlert({
      content: t("detail.confirmDelete"),
      confirmMessage: t("detail.delete"),
      cancelMessage: t("detail.cancel"),
      onConfirm: onDelete,
      onCancel: () => {
        // noop
      },
    });
  }, [doOpenAlert, onDelete]);

  const handleOnReportClick = useCallback(() => {
    setIsReportModalOpen(true);
  }, []);

  const handleOnReportClose = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  const handleOnReportSubmit = useCallback(
    async (data: { title: string; contents: string }) => {
      await onReport(data);
    },
    [onReport],
  );

  if (!dataUser) {
    return null;
  }

  return (
    <>
      <Menu>
        {isMine ? (
          <>
            <MenuItem icon={<IconEdit />} onClick={handleOnEditClick}>
              Edit
            </MenuItem>
            <MenuItem icon={<IconDelete />} onClick={handleOnDeleteClick}>
              Delete
            </MenuItem>
          </>
        ) : (
          <MenuItem icon={<IconExclamation />} onClick={handleOnReportClick}>
            Report
          </MenuItem>
        )}
      </Menu>

      <CreateEditPostModal
        postId={postId}
        category={categoryId}
        defaultValues={{
          categoryId,
          title,
          contents,
        }}
        open={isEditPostModalOpen}
        onClose={handleOnEditModalClose}
        onSubmit={onEdit}
      />

      <ReportModal
        open={isReportModalOpen}
        onSubmit={handleOnReportSubmit}
        onClose={handleOnReportClose}
      />
    </>
  );
};

const getMetaData = (post: ResCommunityPostNews | ResCommunityPostPost) => {
  if (post.post) {
    return {
      title: post.post.title + " | Lawfully",
      description: post.post.contents.substring(0, 160),
      url: `/community/posts/${post.urlSlug}`,
      image: `/share-image-general.jpg`,
    };
  }
  return {
    title: post.news.newsTitle + " | Lawfully",
    image: post.news.newsImage,
    url: `/community/posts/${post.urlSlug}`,
  };
};

const BannerUSCISMessageExplorer: React.FC<{
  uscisTitle: string;
  urlSlug: string;
}> = ({ uscisTitle, urlSlug }) => {
  const t = useTranslations("community");
  const { track } = useEvent();

  const handleOnClick = useCallback(() => {
    track(UIEvent.CommunityPostCommentBannerUSCISStatusExplorerClicked);
  }, [track]);

  return (
    <div
      css={`
        display: flex;
        margin-bottom: 50px;
      `}
    >
      <div
        css={`
          width: 44px;
          height: 44px;
          border: 1px solid #eeeeee;
          display: flex;
          flex-shrink: 0;
          justify-content: center;
          align-items: center;
          border-radius: 22px;
          margin-right: 10px;
        `}
      >
        <IconLogo />
      </div>
      <div
        css={`
          flex: 1;
        `}
      >
        <div
          css={`
            margin-bottom: 2px;
            display: flex;
          `}
        >
          <div
            css={`
              flex: 1;
              margin-right: 5px;
              line-height: 20px;
            `}
          >
            <span
              css={`
                padding-right: 4px;
                font-size: 16px;
                line-height: 24px;
                font-weight: 500;
                word-break: break-word;
              `}
            >
              Lawfully
            </span>
          </div>
        </div>
        <div
          css={`
            white-space: pre-line;
            line-height: 24px;
            font-size: 16px;
            overflow-wrap: break-word;
            word-wrap: break-word;
            word-break: break-word;
            hyphens: auto;
            white-space: pre-line;
            margin-bottom: 10px;
          `}
        >
          {t.rich(
            "detail.explorerBanner",
            { b: (chunks) => <Highlight>{chunks}</Highlight> },
            { message: uscisTitle },
          )}
        </div>
        <a
          href={`/data/uscis-status-explorer/${urlSlug}`}
          target="_blank"
          rel="noreferrer"
          onClick={handleOnClick}
        >
          <Button variant="line" size="small" width="auto">
            {t("detail.findOutNow")}
          </Button>
        </a>
      </div>
    </div>
  );
};

const Highlight = styled.span`
  font-weight: 500;
`;
